import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LogOut, Command, Menu, Settings as SettingsIcon, User as UserIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useSidebar } from "@/contexts/SidebarContext";
import { isElevated } from "@/contexts/AuthContext";
import { vedeImpostazioni } from "@/lib/ambito";
import CentroAvvisi from "@/components/CentroAvvisi";
import CampanellaUnica from "@/components/CampanellaUnica";
import { attrRicerca } from "@/lib/listaColonne";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function TopBar({ user, onLogout }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { openMobile } = useSidebar();

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => { setResults(r.data); setOpen(true); });
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        ref.current?.querySelector("input")?.focus();
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", handler);
    };
  }, []);

  const onSelect = (path) => { setOpen(false); setQ(""); navigate(path); };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
      <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center gap-2 sm:gap-4">
        {/* Hamburger mobile */}
        <button
          onClick={openMobile}
          className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100 text-slate-700"
          aria-label="Apri menu"
          data-testid="topbar-hamburger"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div ref={ref} className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => results && setOpen(true)}
            placeholder="Cerca in tutto l'archivio…"
            className="pl-9 pr-20 h-10 bg-slate-50 border-slate-200"
            data-testid="global-search-input"
            {...attrRicerca("globale")}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-1 text-[10px] text-slate-400 bg-white border border-slate-200 rounded px-1.5 py-0.5">
            <Command className="h-3 w-3" /> K
          </div>

          {open && results && (
            <RisultatiRicerca results={results} elev={isElevated(user)} onSelect={onSelect} />
          )}
        </div>

        <div className="flex items-center gap-2">
          <CentroAvvisi />
          <CampanellaUnica user={user} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100" data-testid="user-menu-btn">
                <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                  {(user?.first_name || user?.name || user?.email || "?").charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-slate-900 leading-tight">
                    {user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user?.name}
                  </div>
                  <div className="text-[11px] text-slate-500">{user?.role_default || user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-medium">
                  {user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user?.name}
                </div>
                <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(`/users/${user?.id}`)} data-testid="profile-btn">
                <UserIcon className="h-4 w-4 mr-2" /> Profilo
              </DropdownMenuItem>
              {vedeImpostazioni(user) && (
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-btn">
                  <SettingsIcon className="h-4 w-4 mr-2" /> Impostazioni
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

// Sprint UI-GLOBALE — risultati della ricerca generale, raggruppati per tipo.
const nomeAnag = (a) => a.ragione_sociale || [a.cognome, a.nome].filter(Boolean).join(" ") || a.codice;
const apriVendita = (x) => (x.vehicle_id ? `/vehicles/${x.vehicle_id}?tab=vendita&vid=${x.id}` : `/vendite/${x.id}`);

function RisultatiRicerca({ results, elev, onSelect }) {
  const gruppi = [
    elev && { key: "companies", titolo: "Aziende", righe: results.companies, path: (c) => `/companies/${c.id}`, testo: (c) => c.name },
    { key: "vehicles", titolo: "Veicoli", righe: results.vehicles, path: (v) => `/vehicles/${v.id}`,
      testo: (v) => [v.codice, v.targa, v.marca, v.modello].filter(Boolean).join(" · ") },
    { key: "anagrafiche", titolo: "Anagrafiche", righe: results.anagrafiche, path: (a) => `/anagrafiche/${a.id}`,
      testo: (a) => [a.codice, nomeAnag(a), a.codice_fiscale || a.partita_iva].filter(Boolean).join(" · ") },
    { key: "preventivi", titolo: "Preventivi", righe: results.preventivi, path: apriVendita,
      testo: (x) => [x.codice, x.veicolo].filter(Boolean).join(" · ") },
    { key: "pratiche", titolo: "Pratiche", righe: results.pratiche, path: apriVendita,
      testo: (x) => [x.codice, x.veicolo].filter(Boolean).join(" · ") },
    { key: "documenti", titolo: "Documenti", righe: results.documenti,
      path: (d) => (d.vehicle_id ? `/vehicles/${d.vehicle_id}?tab=documenti` : null), testo: (d) => d.filename },
  ].filter((g) => g && (g.righe || []).length);
  return (
    <div className="absolute top-12 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[28rem] overflow-y-auto dms-scroll fade-in z-40"
      data-testid="global-search-results">
      {gruppi.length === 0 && <div className="p-4 text-sm text-slate-500" data-testid="global-search-empty">Nessun risultato</div>}
      {gruppi.map((g) => (
        <div key={g.key} className="p-2" data-testid={`global-search-group-${g.key}`}>
          <div className="text-[11px] uppercase text-slate-400 font-medium px-2 py-1">{g.titolo}</div>
          {g.righe.map((r) => {
            const to = g.path(r);
            return (
              <button key={r.id} type="button" disabled={!to} onClick={() => to && onSelect(to)}
                className="w-full text-left px-3 py-2 rounded hover:bg-slate-50 text-sm disabled:opacity-60"
                data-testid={`global-search-${g.key}-${r.id}`}>
                {g.testo(r)}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
